import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';

import { ISiteState, setSiteFetchStatus } from 'features/site/siteSlice';
import {
  deleteFolder,
  fetchFolders,
  IFolder,
  IFolderCreate,
  ILibrary,
  patchFolder,
  postFolder,
} from './folderAPI';

function getFolderId(response: any): number | undefined {
  return response?.response?.data?._id ?? response?.response?.data?.id;
}

export function rebuildFolderTree(library: ILibrary) {
  const allFolders = library.allFolders ?? [];
  const folderMap = new Map<number, IFolder>();
  allFolders.forEach((folder) => {
    folder.subFolders = [];
    if (folder.id != null) folderMap.set(folder.id, folder);
  });
  allFolders.forEach((folder) => {
    const parentId =
      typeof folder.parentFolder === 'object'
        ? folder.parentFolder?.id
        : folder.parentFolder;

    if (parentId != null) {
      const parent = folderMap.get(parentId);
      if (parent) parent.subFolders?.push(folder);
    }
  });
  const rootFolder = allFolders.find((folder) => folder.parentFolder == null);
  library.rootFolder = rootFolder ?? library.rootFolder ?? null;
}

export function validDestinationFolder(
  folderId: number,
  destinationFolder: IFolder,
): boolean {
  if (folderId === destinationFolder.id) return false;

  let currentFolder = destinationFolder.parentFolder as
    | IFolder
    | null
    | undefined;
  while (currentFolder) {
    if (currentFolder.id === folderId) return false;
    currentFolder = currentFolder.parentFolder as IFolder | null | undefined;
  }
  return true;
}

export function findFolder(
  folderId: number | null | undefined,
  library: ILibrary,
): IFolder | null {
  if (folderId === undefined) return null;
  return (
    library.allFolders?.find((folder: IFolder) =>
      folderId === null ? folder.parentFolder === null : folder.id === folderId,
    ) ?? null
  );
}

export const fetchFoldersAsync = createAsyncThunk(
  'folder/fetchFolders',
  async (_, thunkAPI) => {
    try {
      const response = await fetchFolders();
      const folders = response.data ?? [];
      if (folders.length > 0) {
        return { response };
      }
      const rootFolderData: IFolderCreate = {
        folderName: '.',
        parentFolder: null,
      };
      const createdRootFolder = await postFolder(rootFolderData);
      return {
        response,
        createdRootFolder: {
          ...rootFolderData,
          id: createdRootFolder.data?._id ?? createdRootFolder.data?.id,
          parentFolder: null,
          subFolders: [],
          files: [],
        } as IFolder,
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

export const postFolderAsync = createAsyncThunk(
  'folder/postFolder',
  async (
    {
      data,
    }: {
      data: IFolderCreate;
    },
    thunkAPI,
  ) => {
    try {
      const postData: IFolderCreate = {
        ...data,
        folderName: data.folderName ?? 'untitled folder',
      };
      return { response: await postFolder(postData) };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

export const patchFolderAsync = createAsyncThunk(
  'folder/patchFolder',
  async (
    {
      data,
    }: {
      data: IFolder;
    },
    thunkAPI,
  ) => {
    try {
      return { response: await patchFolder(data) };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

export const deleteFolderAsync = createAsyncThunk(
  'folder/deleteFolder',
  async (
    {
      folder,
    }: {
      folder: IFolder;
    },
    thunkAPI,
  ) => {
    try {
      return { response: await deleteFolder(folder.id) };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

function ensureLibrary(state: ISiteState) {
  if (!state.library) {
    state.library = {
      rootFolder: null,
      allFolders: [],
    };
  }
  return state.library;
}

export function addFolderCases(builder: ActionReducerMapBuilder<ISiteState>) {
  builder
    .addCase(fetchFoldersAsync.pending, (state) => {
      state.status = 'loading';
      setSiteFetchStatus(state, 'library', 'loading');
    })
    .addCase(fetchFoldersAsync.fulfilled, (state, action) => {
      state.status = 'idle';
      setSiteFetchStatus(state, 'library', 'idle');
      const library = ensureLibrary(state);
      library.allFolders =
        action.payload.createdRootFolder != null
          ? [action.payload.createdRootFolder]
          : (action.payload.response.data ?? []);
      rebuildFolderTree(library);
    })
    .addCase(fetchFoldersAsync.rejected, (state, action) => {
      state.status = 'failed';
      setSiteFetchStatus(state, 'library', 'failed');
      state.errors = action.payload;
    })
    .addCase(postFolderAsync.pending, (state, action) => {
      state.status = 'loading';
      state.errors = action.payload;
    })
    .addCase(postFolderAsync.fulfilled, (state, action) => {
      state.status = 'idle';
      const library = ensureLibrary(state);
      const id = getFolderId(action.payload);
      if (id == null) return;
      const parent = library.allFolders.find(
        (folder) =>
          folder.id === action.meta.arg.data.parentFolder ||
          (action.meta.arg.data.parentFolder === null &&
            folder.parentFolder === null),
      );
      const data: IFolder = {
        subFolders: [],
        files: [],
        ...action.meta.arg.data,
        id,
        parentFolder: parent,
      };
      library.allFolders.push(data);
      rebuildFolderTree(library);
    })
    .addCase(postFolderAsync.rejected, (state, action) => {
      state.status = 'failed';
      state.errors = action.payload;
    })
    .addCase(patchFolderAsync.pending, (state, action) => {
      state.status = 'loading';
      state.errors = action.payload;
    })
    .addCase(patchFolderAsync.fulfilled, (state, action) => {
      state.status = 'idle';
      const library = ensureLibrary(state);
      const folder = findFolder(action.meta.arg.data.id, library);
      if (folder) {
        Object.assign(folder, action.meta.arg.data);
        rebuildFolderTree(library);
      }
    })
    .addCase(patchFolderAsync.rejected, (state, action) => {
      state.status = 'failed';
      state.errors = action.payload;
    })
    .addCase(deleteFolderAsync.pending, (state, action) => {
      state.status = 'loading';
      state.errors = action.payload;
    })
    .addCase(deleteFolderAsync.fulfilled, (state, action) => {
      state.status = 'idle';
      const library = ensureLibrary(state);
      const folderId = action.meta.arg.folder.id;
      if (folderId == null) return;

      library.allFolders = library.allFolders.filter(
        (folder) => folder.id !== folderId,
      );
      rebuildFolderTree(library);
    })
    .addCase(deleteFolderAsync.rejected, (state, action) => {
      state.status = 'failed';
      state.errors = action.payload;
    });
}
